import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import siteVisitService from '../services/siteVisitService';
import Input from '../components/Input';
import Button from '../components/Button';
import './TechnicianForm.css';

const CompleteSiteVisit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [visit, setVisit] = useState(null);
  const [formData, setFormData] = useState({
    workDescription: '',
    laborCost: '',
    paymentStatus: 'pending',
    customerSignature: '',
  });

  const [itemsReturn, setItemsReturn] = useState([]);
  const [toolsReturn, setToolsReturn] = useState([]);

  useEffect(() => {
    fetchVisit();
  }, [id]);

  const fetchVisit = async () => {
    try {
      const response = await siteVisitService.getById(id);
      const visitData = response.data;
      setVisit(visitData);

      // Initialize items and tools return status with custom quantities
      if (visitData.itemsTaken) {
        setItemsReturn(visitData.itemsTaken.map(item => ({
          ...item,
          returned: item.returned || false,
          returnQuantity: item.returned ? item.quantity : 0, // Quantity to return
          usedQuantity: 0, // Quantity used
        })));
      }
      if (visitData.toolsTaken) {
        setToolsReturn(visitData.toolsTaken.map(tool => ({
          ...tool,
          returned: tool.returned || false
        })));
      }
    } catch (error) {
      toast.error('Failed to fetch site visit details');
      navigate('/site-visits');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleItemReturnToggle = (index) => {
    const updated = [...itemsReturn];
    updated[index].returned = !updated[index].returned;

    // If checking, set return quantity to total taken
    if (updated[index].returned) {
      updated[index].returnQuantity = updated[index].quantity;
      updated[index].usedQuantity = 0;
    } else {
      updated[index].returnQuantity = 0;
      updated[index].usedQuantity = 0;
    }

    setItemsReturn(updated);
  };

  const formatToTwoDecimals = (value) => {
    if (!value) return 0;

    const num = parseFloat(value);
    if (isNaN(num)) return 0;

    return parseFloat(num.toFixed(2));
  };


  // const handleReturnQuantityChange = (index, value) => {
  //   const updated = [...itemsReturn];
  //   const returnQty = Math.min(Math.max(0, parseFloat(value) || 0), updated[index].quantity);
  //   updated[index].returnQuantity = returnQty;
  //   updated[index].usedQuantity = updated[index].quantity - returnQty;
  //   setItemsReturn(updated);
  // };
  const handleReturnQuantityChange = (index, value) => {
    const updated = [...itemsReturn];

    let returnQty = formatToTwoDecimals(value);

    returnQty = Math.min(Math.max(0, returnQty), updated[index].quantity);

    updated[index].returnQuantity = returnQty;
    updated[index].usedQuantity = formatToTwoDecimals(
      updated[index].quantity - returnQty
    );

    setItemsReturn(updated);
  };


  // const handleUsedQuantityChange = (index, value) => {
  //   const updated = [...itemsReturn];
  //   const usedQty = Math.min(Math.max(0, parseFloat(value) || 0), updated[index].quantity);
  //   updated[index].usedQuantity = usedQty;
  //   updated[index].returnQuantity = updated[index].quantity - usedQty;
  //   setItemsReturn(updated);
  // };
  const handleUsedQuantityChange = (index, value) => {
    const updated = [...itemsReturn];

    let usedQty = formatToTwoDecimals(value);

    usedQty = Math.min(Math.max(0, usedQty), updated[index].quantity);

    updated[index].usedQuantity = usedQty;
    updated[index].returnQuantity = formatToTwoDecimals(
      updated[index].quantity - usedQty
    );

    setItemsReturn(updated);
  };

  const handleToolReturnToggle = (index) => {
    const updated = [...toolsReturn];
    updated[index].returned = !updated[index].returned;
    setToolsReturn(updated);
  };

  const calculateMaterialCost = () => {
    if (!itemsReturn || itemsReturn.length === 0) return 0;
    return itemsReturn.reduce((sum, item) => {
      const usedQty = parseFloat(item.usedQuantity) || 0;
      // Prefer per-unit sale price, fall back to inventory price
      const price =
        item.inventory?.unitSalePrice != null && item.inventory.unitSalePrice > 0
          ? item.inventory.unitSalePrice
          : item.inventory?.price || 0;
      return sum + usedQty * price;
    }, 0);
  };

  const calculateTotal = () => {
    const labor = parseFloat(formData.laborCost) || 0;
    const material = calculateMaterialCost();
    return labor + material;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const totalAmount = calculateTotal();
    const materialCost = calculateMaterialCost();

    const submitData = {
      workDescription: formData.workDescription,
      totalCost: {
        laborCost: parseFloat(formData.laborCost) || 0,
        materialCost,
        totalAmount,
      },
      customerSignature: formData.customerSignature || undefined,
    };

    // Update items and tools with return status
    if (visit) {
      submitData.itemsTaken = itemsReturn;
      submitData.toolsTaken = toolsReturn;
    }

    try {
      await siteVisitService.complete(id, submitData);
      toast.success('Site visit completed successfully! Items returned to inventory.');
      navigate('/site-visits');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to complete site visit');
    } finally {
      setLoading(false);
    }
  };

  if (!visit) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="form-container">
      <div className="form-header">
        <button onClick={() => navigate(`/site-visits/${id}`)} className="back-button">
          <FiArrowLeft /> Back
        </button>
        <h2 className="form-title">Complete Site Visit</h2>
        <p style={{ color: 'var(--gray-600)', marginTop: '0.5rem' }}>
          Ticket: #{visit.ticketNumber} - {visit.customer.name}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="form-card">
        <div className="form-section">
          <h3 className="section-title">Work Description</h3>
          <div className="input-group">
            <label className="input-label">
              Describe the work completed <span className="required-asterisk">*</span>
            </label>
            <textarea
              name="workDescription"
              value={formData.workDescription}
              onChange={handleChange}
              placeholder="Detail all work performed, parts replaced, repairs made, etc."
              className="input-field"
              rows="5"
              required
            />
          </div>
        </div>

        <div className="form-section">
          <h3 className="section-title">Cost Breakdown</h3>
          <div className="form-grid">
            <Input
              label="Labor Cost (₹)"
              type="number"
              name="laborCost"
              value={formData.laborCost}
              onChange={handleChange}
              placeholder="1500"
              min="0"
              step="0.01"
              required
            />

            <div className="input-group">
              <label className="input-label">Material Cost (₹)</label>
              <div
                className="input-field"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 600,
                  background: 'var(--gray-50)',
                }}
              >
                ₹{calculateMaterialCost().toFixed(2)}
              </div>
              <small style={{ color: 'var(--gray-500)' }}>
                Auto-calculated from used item quantities × unit sale price
              </small>
            </div>
          </div>

          <div style={{
            padding: '1.5rem',
            background: 'var(--primary-50)',
            borderRadius: 'var(--radius-lg)',
            marginTop: '1rem',
            textAlign: 'center'
          }}>
            <span style={{ fontSize: '0.875rem', color: 'var(--gray-600)', fontWeight: '600' }}>
              TOTAL AMOUNT
            </span>
            <div style={{
              fontSize: '2.5rem',
              fontWeight: '800',
              color: 'var(--primary-700)',
              fontFamily: 'var(--font-display)'
            }}>
              ₹{calculateTotal().toLocaleString()}
            </div>
          </div>

          <div className="input-group" style={{ marginTop: '1rem' }}>
            <label className="input-label">Payment Status</label>
            <select
              name="paymentStatus"
              value={formData.paymentStatus}
              onChange={handleChange}
              className="input-field"
            >
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
            </select>
          </div>
        </div>

        {itemsReturn.length > 0 && (
          <div className="form-section">
            <h3 className="section-title">Items Return - Custom Quantities</h3>
            <p className="section-description">
              Specify how many items were used vs returned. Return quantity will be added back to inventory.
            </p>
            <div className="items-return-grid">
              {itemsReturn.map((item, index) => (
                <div key={index} className="item-return-card">
                  <div className="item-return-header">
                    <label className="expertise-checkbox" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={item.returned}
                        onChange={() => handleItemReturnToggle(index)}
                      />
                      <span className="checkbox-label">{item.itemName}</span>
                    </label>
                    <span className="item-total-badge">Total Taken: {item.quantity}</span>
                  </div>

                  {item.returned && (
                    <div className="quantity-inputs">
                      <div className="quantity-input-group">
                        <label>Used Quantity</label>
                        <input
                          type="number"
                          value={item.usedQuantity}
                          onChange={(e) => handleUsedQuantityChange(index, e.target.value)}
                          min="0"
                          max={item.quantity}
                          step="0.01"
                          className="quantity-input"
                        />
                      </div>
                      <div className="quantity-separator">→</div>
                      <div className="quantity-input-group">
                        <label>Return Quantity</label>
                        <input
                          type="number"
                          value={item.returnQuantity}
                          onChange={(e) => handleReturnQuantityChange(index, e.target.value)}
                          min="0"
                          max={item.quantity}
                          step="0.01"
                          className="quantity-input"
                        />
                      </div>
                    </div>
                  )}

                  {item.returned && (
                    <div className="return-summary">
                      <span className="summary-item">
                        ✓ Returning: <strong>{item.returnQuantity}</strong> to inventory
                      </span>
                      <span className="summary-item">
                        ✗ Used: <strong>{item.usedQuantity}</strong> (not returned)
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {toolsReturn.length > 0 && (
          <div className="form-section">
            <h3 className="section-title">Tools - Mark as Returned</h3>
            <p className="section-description">
              Check tools that were returned
            </p>
            <div className="expertise-grid">
              {toolsReturn.map((tool, index) => (
                <label key={index} className="expertise-checkbox">
                  <input
                    type="checkbox"
                    checked={tool.returned}
                    onChange={() => handleToolReturnToggle(index)}
                  />
                  <span className="checkbox-label">{tool.toolName}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="form-section">
          <h3 className="section-title">Customer Signature (Optional)</h3>
          <Input
            label="Signature"
            type="text"
            name="customerSignature"
            value={formData.customerSignature}
            onChange={handleChange}
            placeholder="Customer signature or acknowledgment"
          />
        </div>

        <div className="form-actions">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(`/site-visits/${id}`)}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            icon={<FiCheckCircle />}
            loading={loading}
          >
            Complete Site Visit
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CompleteSiteVisit;
